import { useMemo } from 'react';
import { DataSource } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { IntegrationType } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import { PersonTypes } from '@frontend/api-person';
import { SchemaIntegrationsService } from '@frontend/schema';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';

export const useGetPersonSourceType = () => {
  const { selectedLocationIds, selectedGroupId } = useAppScopeStore();

  const { data, isError, isLoading, isSuccess } = useScopedQuery({
    queryKey: ['all-integration-details', ...selectedLocationIds, selectedGroupId],
    queryFn: () =>
      Promise.all(
        [...selectedLocationIds, selectedGroupId].map((locationId) =>
          SchemaIntegrationsService.ListDataSourcesByLocationID({ locationId })
        )
      ).then((results) => {
        const integrationsById: Record<string, DataSource> = {};
        results.forEach((result) => {
          result.dataSources?.forEach((datasource) => {
            if (datasource?.sourceId) {
              integrationsById[datasource?.sourceId] = datasource;
            }
          });
        });
        // Return just the unique set of integrations
        return integrationsById;
      }),
  });

  const getPersonSourceType = useMemo(
    () => (person: PersonTypes.Person) => {
      if (!person.SourceID || isLoading || isError || !data) {
        return person.SourceType ?? undefined;
      }

      const integrationDetails = data[person.SourceID];

      if (!integrationDetails) {
        return person.SourceType ?? undefined;
      }

      switch (integrationDetails.integration?.type) {
        case IntegrationType.FILE_UPLOAD:
          return PersonTypes.PersonSourceType.Csv;
        case IntegrationType.MANUAL_ENTRY:
          return PersonTypes.PersonSourceType.CustomContact;
        case IntegrationType.INTEGRATION_TYPE_UNSPECIFIED:
          return undefined;
        default:
          return PersonTypes.PersonSourceType.Integration;
      }
    },
    [data, isLoading, isError]
  );

  return { getPersonSourceType, isSuccess, isLoading, isError };
};
